import moment from "moment";
import { CsvColumn } from "shared/csv";
import { TableColumnGroupedPropsColumn } from "shared/ui-kit";
import wNumb from "wnumb";

import { FieldValuesByCurrency, ScreenerEtfBackendModel } from "./services/model";
import { EtfLabel } from 'shared/ui-kit';

import styles from './columns.module.scss';

const percentFormatter = wNumb({ decimals: 2, suffix: ' %', thousand: ' ' }).to;

function stringColumn(title: string, valueProvider: (etf: ScreenerEtfBackendModel) => string, comparer?: (first: ScreenerEtfBackendModel, second: ScreenerEtfBackendModel) => number): ScreenerColumnDefinition {
	return {
		createTableColumn: () => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf);
				return <>{value}</>;
			},
			comparer: comparer || ((first, second) => {
				const firstValue = valueProvider(first);
				const secondValue = valueProvider(second);

				return firstValue.localeCompare(secondValue);
			})
		}),
		createCsvColumns: () => [{
			title: title,
			valueProvider: valueProvider
		}]
	}
}

function numberFieldByCurrency(title: string, valueProvider: (fields: FieldValuesByCurrency) => number | null, multiplies: number, isColored: boolean) {
	return percentColumnCurrencyDependent(
		title, 
		(etf, currency) => {
			const values = etf.fieldValuesByCurrency[currency];
			if (!values)
				return 0;

			const value = valueProvider(etf.fieldValuesByCurrency[currency]);
			if(value == null)
				return 0;

			return value * multiplies;
		},
		isColored);
}

function percentColumnCurrencyDependent(title: string, valueProvider: (etf: ScreenerEtfBackendModel, currency: string) => number, isColored: boolean): ScreenerColumnDefinition {
	return {
		createTableColumn: (currency: string) => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf, currency);
				if (value === 0)
					return <></>;

				const formattedValue = percentFormatter(value);

				const className = calculateClass(value);

				return <span className={className}>{formattedValue}</span>;
			},
			comparer: (first, second) => {
				const firstValue = valueProvider(first, currency);
				const secondValue = valueProvider(second, currency);
				return secondValue - firstValue;
			},
			classNames: {
				dataCell: styles.column_number
			}
		}),
		createCsvColumns: (currencies: string[]) => currencies.map(currency => ({
			title: `${title},${currency}`,
			valueProvider: etf => percentFormatter(valueProvider(etf, currency))
		}))
	}

	function calculateClass(value: number) {
		if (!isColored)
			return '';
		if (value < 0)
			return styles.percent_negative;
		if (value > 0)
			return styles.percent_positive;
		return '';
	}
}

function percentFieldByCurrency(title: string, valueProvider: (etf: FieldValuesByCurrency) => number | null, multiplies: number, isColored: boolean): ScreenerColumnDefinition {
	return percentColumnCurrencyDependent(
		title,
		(etf, currency) => {
			const values = etf.fieldValuesByCurrency[currency];
			if (!values)
				return 0;

			const value = valueProvider(etf.fieldValuesByCurrency[currency]);
			if(value == null)
				return 0;

			return value * multiplies;
		},
		isColored);
}
function percentColumnCurrencyIndependent(title: string, valueProvider: (etf: ScreenerEtfBackendModel) => number, isColored: boolean): ScreenerColumnDefinition {
	return {
		createTableColumn: () => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf);
				if (value === 0)
					return <></>;

				const formattedValue = percentFormatter(value * 100);

				const className = calculateClass(value);

				return <span className={className}>{formattedValue}</span>;
			},
			comparer: (first, second) => {
				const firstValue = valueProvider(first);
				const secondValue = valueProvider(second);
				return secondValue - firstValue;
			},
			classNames: {
				dataCell: styles.column_number
			}
		}),
		createCsvColumns: () => [{
			title: title,
			valueProvider: etf => percentFormatter(valueProvider(etf))
		}]
	}

	function calculateClass(value: number) {
		if (!isColored)
			return '';
		if (value < 0)
			return styles.percent_negative;
		if (value > 0)
			return styles.percent_positive;
		return '';
	}
}

function amountColumnCurrencyDependent(title: string, valueProvider: (etf: ScreenerEtfBackendModel, currency: string) => number, decimals: number): ScreenerColumnDefinition {
	const formatter = wNumb({ decimals: decimals, suffix: '', thousand: ' ' }).to;

	return {
		createTableColumn: currency => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf, currency);
				if (value === 0)
					return <></>;

				const formattedValue = formatter(value);
	
				return <span>{formattedValue}</span>;
			},
			comparer: (first, second) => {
				const firstValue = valueProvider(first, currency);
				const secondValue = valueProvider(second, currency);
				return secondValue - firstValue;
			},
			classNames: {
				dataCell: styles.column_number
			}
		}),
		createCsvColumns: currencies => currencies.map(currency => ({
			title: `${title},${currency}`,
			valueProvider: etf => formatter(valueProvider(etf, currency))
		}))
	};
}

function amountFieldByCurrency(title: string, valueProvider: (fields: FieldValuesByCurrency) => number | null, multiplies: number, decimals: number) {
	return amountColumnCurrencyDependent(
		title,
		(etf, currency) => {
			const values = etf.fieldValuesByCurrency[currency];
			if (!values)
				return 0;

			const value = valueProvider(etf.fieldValuesByCurrency[currency]);
			if(value == null)
				return 0;

			return value * multiplies;
		},
		decimals);
}


function amountColumnCurrencyIndependent(title: string, valueProvider: (etf: ScreenerEtfBackendModel) => number, decimals: number): ScreenerColumnDefinition {
	const formatter = wNumb({ decimals: decimals, suffix: '', thousand: ' ' }).to;

	return {
		createTableColumn: () => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf);
				const formattedValue = formatter(value);
	
				return <span>{formattedValue}</span>;
			},
			comparer: (first, second) => {
				const firstValue = valueProvider(first);
				const secondValue = valueProvider(second);
				return secondValue - firstValue;
			},
			classNames: {
				dataCell: styles.column_number
			}
		}),
		createCsvColumns: () => [{
			title: title,
			valueProvider: etf => formatter(valueProvider(etf))
		}]
	};
}

function dateColumn(title: string, valueProvider: (etf: ScreenerEtfBackendModel) => Date): ScreenerColumnDefinition {
	return {
		createTableColumn: () => ({
			title: title,
			renderCell: etf => {
				const value = valueProvider(etf);
				const formattedValue = moment(value).format('D MMM YYYY');
	
				return <span>{formattedValue}</span>;
			},
			comparer: (first, second) => {
				const firstValue = valueProvider(first);
				const secondValue = valueProvider(second);
				return secondValue.getTime() - firstValue.getTime();
			},
			classNames: {
				dataCell: styles.column_number
			}
		}),
		createCsvColumns: () => [{
			title: title,
			valueProvider: etf => moment(valueProvider(etf)).format('D MMM YYYY')
		}]
	};
}

function reportColumn(title: string, options: string): ScreenerColumnDefinition {
	return {
		createTableColumn: () => ({
			title: title,
			renderCell: etf => {
				const href = `/etf/report/${etf.ticker}?options=${options}`;
	
				return <a href={href} target="_blank">{title}</a>;
			}
		}),
		createCsvColumns: () => []
	};
}

export interface ScreenerColumnDefinition {
	createTableColumn(currency: string): TableColumnGroupedPropsColumn<ScreenerEtfBackendModel>;
	createCsvColumns(currencies: string[]): CsvColumn<ScreenerEtfBackendModel>[];
}
export interface ScreenerColumnGroupDefinition {
	title: string;
	isExpandedByDefault: boolean;
	columns: ScreenerColumnDefinition[];
}
export interface ScreenerTableDefinition {
	ticker: ScreenerColumnDefinition;
	groups: ScreenerColumnGroupDefinition[];
}

export const ScreenerTable: ScreenerTableDefinition = {
	ticker: {
		createTableColumn: () => ({
			title: 'Тикер',
			renderCell: etf => <EtfLabel ticker={etf.ticker} allowNavigation />,
			comparer: (first, second) => first.ticker.localeCompare(second.ticker)
		}),
		createCsvColumns: () => [{
			title: 'Тикер',
			valueProvider: etf => etf.ticker
		}]
	},
	groups: [
		{
			title: 'Основное',
			isExpandedByDefault: true,
			columns: [
				{
					createTableColumn: () => ({
						title: <i className="fa fa-flag"></i>,
						renderCell: etf => <span className={`flag-icon flag-icon-${etf.tags.geography.flag}`}></span>
					}),
					createCsvColumns: () => []
				},
				stringColumn('Категория', etf => etf.tags.investmentPolicy.assetClass),
				stringColumn('Эмитент', etf => etf.issuer.name)
			]
		},
		{
			title: 'География',
			isExpandedByDefault: false,
			columns: [
				stringColumn('Регион', etf => etf.tags.geography.area),
				stringColumn('Страна', etf => etf.tags.geography.country),
				stringColumn('Рынок', etf => etf.tags.geography.marketType)
			]
		},
		{
			title: 'Активы',
			isExpandedByDefault: false,
			columns: [
				stringColumn('Класс', etf => etf.tags.investmentPolicy.assetClass),
				stringColumn('Категория', etf => etf.tags.investmentPolicy.assetSubClass),
				stringColumn('Валюта', etf => etf.tags.investmentPolicy.assetCurrency)
			]
		},
		{
			title: 'Доходность',
			isExpandedByDefault: true,
			columns: [
				numberFieldByCurrency('1 день', values => values['metric/return/1day'], 100, true),
				numberFieldByCurrency('1 неделя', values => values['metric/return/1week'], 100, true),
				numberFieldByCurrency('1 месяц', values => values['metric/return/1month'], 100, true),
				numberFieldByCurrency('1 квартал', values => values['metric/return/3month'], 100, true),
				numberFieldByCurrency('1 год', values => values['metric/return/1year'], 100, true),
				stringColumn('Статус фонда', etf => etf.tradeStatus?.name || '-', (etf1, etf2) => (etf1.tradeStatus?.sortOrder || 0) - (etf2.tradeStatus?.sortOrder || 0))
			]
		},
		{
			title: 'Состав',
			isExpandedByDefault: false,
			columns: [
				amountColumnCurrencyIndependent('Количество', etf => etf.assets.totalCount, 0),
				percentColumnCurrencyIndependent('Топ 3%', etf => etf.assets.top3Percent, false),
				percentColumnCurrencyIndependent('Топ 5%', etf => etf.assets.top5Percent, false),
				percentColumnCurrencyIndependent('Топ 10%', etf => etf.assets.top10Percent, false),
				percentColumnCurrencyIndependent('Топ 50%', etf => etf.assets.top50Percent, false),
			]
		},
		{
			title: 'Комиссия и СЧА',
			isExpandedByDefault: false,
			columns: [
				amountColumnCurrencyDependent('СЧА', (etf, currency) => etf.assetsUnderManagementByCurrency[currency] || 0, 0),
				percentColumnCurrencyIndependent('Комиссия', etf => etf.commisionPercent, false),
				stringColumn('Отчетная валюта', etf => etf.currency.name),
				stringColumn('Политика дивидендов', etf => etf.tags.investmentPolicy.dividendPolicy),
				dateColumn('Начало торговли', etf => etf.tradingStartDate)
			]
		},
		{
			title: 'Риск',
			isExpandedByDefault: false,
			columns: [
				amountFieldByCurrency('ШАРП', values => values["metric/sharpe"], 1, 2),
				amountFieldByCurrency('Сортино', values => values["metric/sortino"], 1, 2),
				percentFieldByCurrency('VaR 5%', values => values['metric/var'], 100, false)
			]
		},
		{
			title: 'Отчеты',
			isExpandedByDefault: false,
			columns: [
				reportColumn('vs S&P 500', 'vs=^SP500TR'),
				reportColumn('vs IMOEX', 'vs=MCFTR'),
				reportColumn('vs Инфляция', 'vs=Inflation&rf=0.'),
				reportColumn('vs Депозит', 'vs=Deposit&rf=0.')
			]
		}
	]
}
